import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  gameService,
  GetGamesParams,
  CreateGameRequest,
  UpdateGameRequest,
} from "../api/services/games/gameService";

const GAMES_STALE_TIME =
  Number(process.env.REACT_APP_GAMES_STALE_TIME) || 5 * 60 * 1000;

export const gameKeys = {
  all: ["games"] as const,
  lists: () => [...gameKeys.all, "list"] as const,
  list: (params: GetGamesParams) => [...gameKeys.lists(), params] as const,
  details: () => [...gameKeys.all, "detail"] as const,
  detail: (id: string) => [...gameKeys.details(), id] as const,
};

export const useGames = (params: GetGamesParams = {}) => {
  return useQuery({
    queryKey: gameKeys.list(params),
    queryFn: () => gameService.getGames(params),
    staleTime: GAMES_STALE_TIME,
  });
};

export const useGame = (id: string) => {
  return useQuery({
    queryKey: gameKeys.detail(id),
    queryFn: () => gameService.getGame(id),
    enabled: !!id,
    staleTime: GAMES_STALE_TIME,
  });
};

export const useCreateGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGameRequest) => gameService.createGame(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gameKeys.lists() });
    },
  });
};

export const useUpdateGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      gameId,
      data,
    }: {
      gameId: string;
      data: UpdateGameRequest;
    }) => gameService.updateGame(gameId, data),
    onSuccess: (_, { gameId }) => {
      queryClient.invalidateQueries({ queryKey: gameKeys.lists() });
      queryClient.invalidateQueries({ queryKey: gameKeys.detail(gameId) });
    },
  });
};

export const useDeleteGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (gameId: string) => gameService.deleteGame(gameId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gameKeys.lists() });
    },
  });
};

export const useGameManagement = (params: GetGamesParams = {}) => {
  const {
    data: gamesData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGames(params);

  const createGame = useCreateGame();
  const updateGame = useUpdateGame();
  const deleteGame = useDeleteGame();

  return {
    games: gamesData?.games || [],
    total: gamesData?.total || 0,
    isLoading,
    isError,
    error,
    createGame: createGame.mutate,
    updateGame: updateGame.mutate,
    deleteGame: deleteGame.mutate,
    isCreating: createGame.isPending,
    isUpdating: updateGame.isPending,
    isDeleting: deleteGame.isPending,
    createError: createGame.error,
    updateError: updateGame.error,
    deleteError: deleteGame.error,
    refreshGames: refetch,
  };
};
